import { Constants } from '../common/Constants';
import * as Enums from '../common/Enumerations';
import { QualtricsContextData } from '../common/InternalTypes';
import { Logger } from '../common/Logger';
import * as Types from '../common/Types';
import { Util } from '../common/Util';

export class AppContextDataContainer {
	private readonly _logger: Logger;
	private _appId: string;
	private _appTitle?: string;
	private _appVersion?: string;
	private _appSupportInfo?: string;
	private _technicalAppComponentId?: string;
	private _appFrameworkId?: string;
	private _appFrameworkVersion?: string;
	private _theme?: string;
	private _previousTheme?: string;
	private _languageTag: string;
	private _clientAction: Enums.CLIENT_ACTION;

	constructor() {
		this._logger = new Logger(Constants.FILE_APP_CONTEXT_DATA_CONTAINER);
	}

	public static create(appContextData: Types.AppContextData, clientAction = Enums.CLIENT_ACTION.none): AppContextDataContainer {
		const container = new AppContextDataContainer();
		if (!appContextData) {
			appContextData = {} as Types.AppContextData;
		}
		container.appId = appContextData.appId;
		container.appTitle = appContextData.appTitle;
		container.appVersion = appContextData.appVersion;
		container.appSupportInfo = appContextData.appSupportInfo;
		container.technicalAppComponentId = appContextData.technicalAppComponentId;
		container.appFrameworkId = appContextData.appFrameworkId;
		container.appFrameworkVersion = appContextData.appFrameworkVersion;
		container.theme = appContextData.theme;
		container.previousTheme = appContextData.previousTheme;
		container.languageTag = appContextData.languageTag;
		container.clientAction = clientAction;
		return container;
	}

	public toQualtricsContextData(): QualtricsContextData {
		const contextData = {};
		contextData['appId'] = this.appId;
		contextData['appTitle'] = this.appTitle;
		contextData['appVersion'] = this.appVersion;
		contextData['appSupportInfo'] = this.appSupportInfo;
		contextData['technicalAppComponentId'] = this.technicalAppComponentId;
		contextData['appFrameworkId'] = this.appFrameworkId;
		contextData['appFrameworkVersion'] = this.appFrameworkVersion;
		contextData['theme'] = this.theme;
		contextData['previousTheme'] = this.previousTheme;
		contextData['languageTag'] = this.languageTag;
		contextData[Constants.CLIENT_ACTION] = this.clientAction;
		return contextData;
	}

	set appId(value: string) {
		this._appId = value;
	}
	get appId(): string {
		const isValidString = this._isValidNonEmptyString(this._appId);
		if (isValidString) {
			return this._appId;
		}
		this._logger.info(`appId is not valid or missing, "${Constants.VALUE_NOT_AVAILABLE}" will be assigned to it.`);
		return Constants.VALUE_NOT_AVAILABLE;
	}

	set appTitle(value: string | undefined) {
		this._appTitle = value;
	}
	get appTitle(): string {
		const isValidString = this._isValidNonEmptyString(this._appTitle);
		if (isValidString) {
			return this._appTitle!;
		}
		this._logger.info(`appTitle is not valid or missing, "${Constants.VALUE_NOT_AVAILABLE}" will be assigned to it.`);
		return Constants.VALUE_NOT_AVAILABLE;
	}

	set appVersion(value: string | undefined) {
		this._appVersion = value;
	}
	get appVersion(): string {
		const isValidString = this._isValidNonEmptyString(this._appVersion);
		if (isValidString) {
			return this._appVersion!;
		}
		this._logger.info(`appVersion is not valid or missing, "${Constants.VALUE_NOT_AVAILABLE}" will be assigned to it.`);
		return Constants.VALUE_NOT_AVAILABLE;
	}

	set appSupportInfo(value: string | undefined) {
		this._appSupportInfo = value;
	}
	get appSupportInfo(): string {
		const formattedAppSupportInfo = Util.getTrimmedUppercaseString(this._appSupportInfo!);
		if (formattedAppSupportInfo) {
			return formattedAppSupportInfo;
		}
		this._logger.info(`appSupportInfo is not valid or missing, "${Constants.VALUE_NOT_AVAILABLE}" will be assigned to it.`);
		return Constants.VALUE_NOT_AVAILABLE;
	}

	set technicalAppComponentId(value: string | undefined) {
		this._technicalAppComponentId = value;
	}
	get technicalAppComponentId(): string {
		const isValidString = this._isValidNonEmptyString(this._technicalAppComponentId);
		if (isValidString) {
			return this._technicalAppComponentId!;
		}
		this._logger.info(`technicalAppComponentId is not valid or missing, "${Constants.VALUE_NOT_AVAILABLE}" will be assigned to it.`);
		return Constants.VALUE_NOT_AVAILABLE;
	}

	set appFrameworkId(value: string | undefined) {
		this._appFrameworkId = value;
	}
	get appFrameworkId(): string {
		const isValidString = this._isValidNonEmptyString(this._appFrameworkId);
		if (isValidString) {
			return this._appFrameworkId!;
		}
		this._logger.info(`appFrameworkId is not valid or missing, "${Constants.VALUE_NOT_AVAILABLE}" will be assigned to it.`);
		return Constants.VALUE_NOT_AVAILABLE;
	}

	set appFrameworkVersion(value: string | undefined) {
		this._appFrameworkVersion = value;
	}
	get appFrameworkVersion(): string {
		const isValidString = this._isValidNonEmptyString(this._appFrameworkVersion);
		if (isValidString) {
			return this._appFrameworkVersion!;
		}
		this._logger.info(`appFrameworkVersion is not valid or missing, "${Constants.VALUE_NOT_AVAILABLE}" will be assigned to it.`);
		return Constants.VALUE_NOT_AVAILABLE;
	}

	set theme(value: string | undefined) {
		this._theme = value;
	}
	get theme(): string {
		const formattedThemeValue = Util.getTrimmedLowercaseString(this._theme!);
		if (formattedThemeValue) {
			return formattedThemeValue;
		}
		this._logger.info(`theme is not valid or missing, "${Constants.VALUE_NOT_AVAILABLE}" will be assigned to it.`);
		return Constants.VALUE_NOT_AVAILABLE;
	}

	set previousTheme(value: string | undefined) {
		this._previousTheme = value;
	}
	get previousTheme(): string {
		const formattedPreviousThemeValue = Util.getTrimmedLowercaseString(this._previousTheme!);
		if (formattedPreviousThemeValue) {
			return formattedPreviousThemeValue;
		}
		this._logger.info(`previousTheme is not valid or missing, "${Constants.VALUE_NOT_AVAILABLE}" will be assigned to it.`);
		return Constants.VALUE_NOT_AVAILABLE;
	}

	set languageTag(value: string) {
		this._languageTag = value;
	}
	get languageTag(): string {
		const trimmedUppercaseString = Util.getTrimmedUppercaseString(this._languageTag);
		if (trimmedUppercaseString && trimmedUppercaseString.length > 0) {
			return trimmedUppercaseString;
		}
		this._logger.info(`languageTag is not valid or missing, "${Constants.VALUE_NOT_AVAILABLE}" will be assigned to it.`);
		return Constants.VALUE_NOT_AVAILABLE;
	}

	public set clientAction(value: Enums.CLIENT_ACTION) {
		this._clientAction = value;
	}
	get clientAction(): Enums.CLIENT_ACTION {
		return this._clientAction;
	}

	private _isValidNonEmptyString(value: any) {
		return Util.isString(value) && !Util.stringIsEmpty(value);
	}
}
